import { initializeBindings } from "https://static.cloud.coveo.com/atomic/v3/index.esm.js";
import { buildPager } from "https://static.cloud.coveo.com/headless/v3/headless.esm.js";

class CustomPager extends HTMLElement {
	async connectedCallback() {
		this.bindings = await initializeBindings(this);

		this.pager = buildPager(this.bindings.engine, {
			options: {
				numberOfPages: 7
			}
		});

		this.pager.subscribe(() => this.render());
		this.render();
	}

	render() {
		const state = this.pager.state || {};
		const currentPage = this.getCurrentPage(state);
		const totalPages = this.getTotalPages(state);
		const pagerShell = this.querySelector(":scope > .pager");
		const pagerContent = this.querySelector(":scope > .pager .pager-content") || pagerShell;
		const pageButtonTemplate = this.querySelector(':scope > template[data-role="page-button"]');
		const previousButtonTemplate = this.querySelector(':scope > template[data-role="previous-button"]');
		const nextButtonTemplate = this.querySelector(':scope > template[data-role="next-button"]');

		if (!pagerContent) {
			console.warn("custom-pager requires inline .pager markup in page HTML");
			return;
		}

		if (totalPages <= 1) {
			pagerContent.innerHTML = "";
			if (pagerShell) {
				pagerShell.hidden = true;
			}
			return;
		}

		if (pagerShell) {
			pagerShell.hidden = false;
		}

		const showPrev = currentPage > 1;
		const showNext = currentPage < totalPages;
		const mainPages = this.getMainPages(currentPage, totalPages);

		pagerContent.replaceChildren();

		if (showPrev) {
			pagerContent.append(this.createNavButton(previousButtonTemplate));
		}

		mainPages.forEach((pageNumber) => {
			const isCurrent = currentPage === pageNumber;
			pagerContent.append(this.createPageButton(pageNumber, isCurrent, pageButtonTemplate));
		});

		if (showNext) {
			pagerContent.append(this.createNavButton(nextButtonTemplate));
		}

		if (this.pendingFocusPage === currentPage) {
			pagerContent.querySelector(`button[data-page="${currentPage}"]`)?.focus();
			this.pendingFocusPage = undefined;
		}

		pagerContent.querySelectorAll("button[data-page]").forEach((button) => {
			button.addEventListener("click", (event) => {
				const page = Number(button.dataset.page);
				if (!page || page === currentPage) {
					return;
				}
				this.pendingFocusPage = event.detail === 0 ? page : undefined;
				this.pager.selectPage(page);
			});
		});

		const prevButton = pagerContent.querySelector("button[data-nav=\"prev\"]");
		if (prevButton) {
			prevButton.addEventListener("click", () => {
				this.pager.previousPage();
			});
		}

		const nextButton = pagerContent.querySelector("button[data-nav=\"next\"]");
		if (nextButton) {
			nextButton.addEventListener("click", () => {
				this.pager.nextPage();
			});
		}
	}

	createPageButton(pageNumber, isCurrent, template) {
		const button = template?.content?.firstElementChild
			? template.content.firstElementChild.cloneNode(true)
			: document.createElement("button");
		const labelTemplate = button instanceof HTMLButtonElement
			? isCurrent ? button.dataset.currentPageLabel : button.dataset.pageLabel
			: "";
		const accessibleLabel = labelTemplate?.replace("{pageNumber}", String(pageNumber));

		if (!(button instanceof HTMLButtonElement)) {
			const fallback = document.createElement("button");
			fallback.type = "button";
			fallback.className = "page";
			fallback.dataset.page = String(pageNumber);
			fallback.textContent = String(pageNumber);
			if (isCurrent) {
				fallback.classList.add("current");
				fallback.setAttribute("aria-current", "page");
			}
			return fallback;
		}

		button.dataset.page = String(pageNumber);
		button.textContent = String(pageNumber);
		if (accessibleLabel) {
			button.setAttribute("aria-label", accessibleLabel);
		}
		button.classList.add("page");
		button.classList.toggle("current", isCurrent);
		if (isCurrent) {
			button.setAttribute("aria-current", "page");
		} else {
			button.removeAttribute("aria-current");
		}
		return button;
	}

	createNavButton(template) {
		const button = template?.content?.firstElementChild?.cloneNode(true);

		if (!(button instanceof HTMLButtonElement)) {
			console.warn("custom-pager requires previous and next button templates in page HTML");
			return document.createElement("button");
		}

		return button;
	}

	createEllipsis(template) {
		const ellipsis = template?.content?.firstElementChild
			? template.content.firstElementChild.cloneNode(true)
			: document.createElement("span");

		if (!(ellipsis instanceof HTMLElement)) {
			const fallback = document.createElement("span");
			fallback.className = "ellipsis";
			fallback.setAttribute("aria-hidden", "true");
			fallback.textContent = "...";
			return fallback;
		}

		return ellipsis;
	}

	getMainPages(currentPage, totalPages) {
		const windowSize = Math.min(5, totalPages);
		let start = currentPage - Math.floor(windowSize / 2);
		let end = start + windowSize - 1;

		if (start < 1) {
			start = 1;
			end = windowSize;
		}

		if (end > totalPages) {
			end = totalPages;
			start = Math.max(1, end - windowSize + 1);
		}

		return Array.from({ length: end - start + 1 }, (_, index) => start + index);
	}

	getCurrentPage(state) {
		if (typeof state.currentPage === "number" && state.currentPage > 0) {
			return state.currentPage;
		}

		const currentFromList = (state.currentPages || []).find((page) => page.isCurrent);
		return currentFromList?.page || 1;
	}

	getTotalPages(state) {
		if (typeof state.maxPage === "number" && state.maxPage > 0) {
			return state.maxPage;
		}

		const pages = state.currentPages || [];
		const maxFromPages = pages.reduce((maxValue, page) => Math.max(maxValue, page.page || 0), 0);
		return Math.max(maxFromPages, 1);
	}
}

if (!window.customElements.get("custom-pager")) {
	window.customElements.define("custom-pager", CustomPager);
}
